import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode
} from 'libphonenumber-js';

export type { CountryCode };

export const DEFAULT_COUNTRY: CountryCode = 'NO';

/** Listed first, in this order, then a separator, then everyone else by name. */
export const PREFERRED_COUNTRIES: CountryCode[] = ['NO', 'SE', 'DK', 'FI', 'IS'];

/**
 * E.164 for a national number typed in the given country (spaces, dashes and a leading 0 are
 * fine), or `null` when it is empty or not a valid number there.
 */
export const toE164 = (country: string, national: string): string | null => {
  if (national.trim() === '') {
    return null;
  }
  const parse = (value: string): string | null => {
    const parsed = parsePhoneNumberFromString(value, country as CountryCode);
    return parsed?.isValid() === true ? parsed.number : null;
  };
  // Countries without a trunk prefix (Norway) reject a leading 0 that people type out of habit.
  return parse(national) ?? parse(national.trim().replace(/^0+/, ''));
};

export interface CountryOption {
  code: CountryCode;
  callingCode: string;
  name: string;
}

/** Every country, localised; the preferred ones first (in order), the rest sorted by name. */
export const countryOptions = (
  locale: string
): { preferred: CountryOption[]; others: CountryOption[] } => {
  const names = new Intl.DisplayNames([locale], { type: 'region' });
  const all = getCountries().map((code) => ({
    code,
    callingCode: getCountryCallingCode(code),
    name: names.of(code) ?? code
  }));
  return {
    preferred: PREFERRED_COUNTRIES.flatMap((code) => all.filter((o) => o.code === code)),
    others: all
      .filter((o) => !PREFERRED_COUNTRIES.includes(o.code))
      .sort((a, b) => a.name.localeCompare(b.name, locale))
  };
};

/** Matches name, ISO code and calling code; "47", "+47", "nor" and "NO" all find Norway. */
export const matchesCountry = (o: CountryOption, query: string): boolean => {
  const q = query.trim().toLocaleLowerCase();
  if (q === '') {
    return true;
  }
  const digits = q.replace(/^\+/, '');
  return (
    o.name.toLocaleLowerCase().includes(q) ||
    o.code.toLowerCase().startsWith(q) ||
    (digits !== '' && o.callingCode.startsWith(digits))
  );
};
